import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as LinkIcon, Loader2 } from "lucide-react";
import { z } from "zod";

import { trpc } from "@/lib/trpc";
import { getAppPageRouteDecision } from "@/lib/app-page-routing";
import {
  consumeLandingWebsiteIntent,
  peekLandingWebsiteIntent,
} from "@/lib/landing-website-intent";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { Button } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";

const urlSchema = z
  .string()
  .trim()
  .transform((value) => (!/^https?:\/\//i.test(value) ? `https://${value}` : value))
  .pipe(z.url())
  .refine((value) => new URL(value).hostname.split(".").filter(Boolean).length >= 2, {
    message: "Please enter a valid website URL.",
  });

export default function AppPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const autoSubmittedWebsiteRef = useRef(false);
  const utils = trpc.useUtils();

  const brandQuery = trpc.brand.listMine.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const createBrand = trpc.brand.createMine.useMutation({
    onSuccess: async (result) => {
      setError("");
      await utils.brand.listMine.invalidate();
      navigate(buildBrandPath(result.brand.id, "/onboarding"), { replace: true });
    },
    onError: (mutationError) => {
      setError(mutationError.message);
    },
  });

  const brands = brandQuery.data?.brands ?? [];
  const routeDecision = getAppPageRouteDecision(brands);
  const canCreateFromLandingWebsiteIntent =
    !brandQuery.isPending &&
    Boolean(brandQuery.data?.canCreate) &&
    Boolean(peekLandingWebsiteIntent());
  const isHandlingLandingWebsiteIntent =
    canCreateFromLandingWebsiteIntent || autoSubmittedWebsiteRef.current;

  useEffect(() => {
    if (
      brandQuery.isPending ||
      createBrand.isPending ||
      autoSubmittedWebsiteRef.current ||
      !canCreateFromLandingWebsiteIntent
    ) {
      return;
    }

    const sourceUrl = consumeLandingWebsiteIntent();
    if (!sourceUrl) {
      return;
    }

    autoSubmittedWebsiteRef.current = true;
    setValue(sourceUrl);
    setError("");
    createBrand.mutate({ sourceUrl });
  }, [brandQuery.isPending, canCreateFromLandingWebsiteIntent, createBrand]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");

    const result = urlSchema.safeParse(value);
    if (!result.success) {
      setError(
        intl.formatMessage({
          id: "origin.invalidUrl",
          defaultMessage: "Please enter a valid website URL.",
        }),
      );
      return;
    }

    createBrand.mutate({
      sourceUrl: result.data,
    });
  }

  if (brandQuery.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isHandlingLandingWebsiteIntent && routeDecision.kind === "redirect") {
    return <Navigate to={routeDecision.to} replace />;
  }

  if (
    !isHandlingLandingWebsiteIntent &&
    routeDecision.kind === "first_onboarding" &&
    routeDecision.brandId
  ) {
    return <Navigate to={buildBrandPath(routeDecision.brandId, "/onboarding")} replace />;
  }

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4">
      <div className="w-full max-w-xl space-y-8 text-center">
        <div className="space-y-3">
          <h1 className="font-display text-5xl font-bold leading-tight tracking-tight sm:text-6xl">
            <FormattedMessage id="origin.heroTitle" defaultMessage="Let's build your brand." />
          </h1>
          <p className="mx-auto max-w-md text-base text-muted-foreground">
            <FormattedMessage
              id="origin.heroSubtitle"
              defaultMessage="Enter your website URL to instantly generate a deep brand profile and strategic content."
            />
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mx-auto max-w-lg">
          <div className="flex items-center gap-2 rounded-full border border-border bg-card p-2 shadow-soft transition-shadow focus-within:shadow-deep focus-within:ring-2 focus-within:ring-peach/50">
            <LinkIcon className="ml-3 h-5 w-5 shrink-0 text-muted-foreground" />
            <Input
              type="text"
              placeholder={intl.formatMessage({
                id: "origin.urlPlaceholder",
                defaultMessage: "paste your website url...",
              })}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              className="flex-1 border-0 bg-transparent text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
              disabled={createBrand.isPending}
            />
            <Button
              type="submit"
              className="rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              disabled={createBrand.isPending}
            >
              {createBrand.isPending ? (
                <FormattedMessage id="common.loading" defaultMessage="Loading…" />
              ) : (
                <FormattedMessage id="origin.buildProfile" defaultMessage="Build Profile →" />
              )}
            </Button>
          </div>
          {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
        </form>
      </div>
    </div>
  );
}
